import React, { createContext, useState, useEffect, useContext } from 'react';
import supabase from '../supabaseClient';
import { useUser } from './UserContext';
import toast from 'react-hot-toast';

// Mock data for when Supabase connection fails
const MOCK_CATEGORIES = [
  { id: '1', name: 'Cleaning Supplies', description: 'Products used for cleaning and maintenance' },
  { id: '2', name: 'Guest Amenities', description: 'Items provided to guests in rooms' },
  { id: '3', name: 'Office Supplies', description: 'General office materials' },
  { id: '4', name: 'Kitchen Supplies', description: 'Items used in the kitchen and restaurant' },
  { id: '5', name: 'Maintenance', description: 'Tools and materials for property maintenance' },
  { id: '6', name: 'Linens', description: 'Bed sheets, towels, and other fabric items' }
];

const MOCK_ITEMS = [
  {
    id: '1',
    name: 'Bath Towels',
    description: 'Standard white bath towels',
    category_id: '6',
    sku: 'LIN-BT-001',
    unit_of_measure: 'piece',
    minimum_stock: 50,
    reorder_point: 100,
    current_stock: 250,
    unit_cost: 8.50,
    supplier: 'Luxury Linens Co.',
    location: 'Linen Storage Room',
    inventory_categories: { id: '6', name: 'Linens' }
  },
  {
    id: '2',
    name: 'Toilet Paper',
    description: '2-ply toilet paper rolls',
    category_id: '2',
    sku: 'AM-TP-002',
    unit_of_measure: 'roll',
    minimum_stock: 100,
    reorder_point: 200,
    current_stock: 450,
    unit_cost: 0.75,
    supplier: 'Hotel Supplies Inc.',
    location: 'Main Storage',
    inventory_categories: { id: '2', name: 'Guest Amenities' }
  },
  {
    id: '3',
    name: 'Liquid Soap',
    description: 'Hand soap for dispensers',
    category_id: '1',
    sku: 'CL-LS-003',
    unit_of_measure: 'liter',
    minimum_stock: 10,
    reorder_point: 20,
    current_stock: 35,
    unit_cost: 3.25,
    supplier: 'CleanPro Supplies',
    location: 'Cleaning Closet',
    inventory_categories: { id: '1', name: 'Cleaning Supplies' }
  },
  {
    id: '4',
    name: 'Light Bulbs',
    description: 'LED light bulbs, 60W equivalent',
    category_id: '5',
    sku: 'MT-LB-004',
    unit_of_measure: 'piece',
    minimum_stock: 30,
    reorder_point: 50,
    current_stock: 85,
    unit_cost: 2.50,
    supplier: 'Electrical Supply Co.',
    location: 'Maintenance Room',
    inventory_categories: { id: '5', name: 'Maintenance' }
  },
  {
    id: '5',
    name: 'Coffee Pods',
    description: 'Single-serve coffee pods for in-room coffee makers',
    category_id: '2',
    sku: 'AM-CP-005',
    unit_of_measure: 'piece',
    minimum_stock: 200,
    reorder_point: 350,
    current_stock: 500,
    unit_cost: 0.35,
    supplier: 'Gourmet Coffee Distributors',
    location: 'Pantry Storage',
    inventory_categories: { id: '2', name: 'Guest Amenities' }
  }
];

const MOCK_TRANSACTIONS = [
  {
    id: '1',
    item_id: '1',
    transaction_type: 'purchase',
    quantity: 100,
    unit_price: 8.50,
    total_price: 850,
    reference_number: 'PO-2025-001',
    notes: 'Regular monthly order',
    department: 'Housekeeping',
    transaction_date: '2025-03-15T10:00:00Z',
    item: { name: 'Bath Towels', sku: 'LIN-BT-001' }
  },
  {
    id: '2',
    item_id: '2',
    transaction_type: 'consumption',
    quantity: 50,
    unit_price: 0.75,
    total_price: 37.5,
    reference_number: 'REQ-2025-042',
    notes: 'Weekly room supply',
    department: 'Housekeeping',
    transaction_date: '2025-03-20T14:30:00Z',
    item: { name: 'Toilet Paper', sku: 'AM-TP-002' }
  },
  {
    id: '3',
    item_id: '3',
    transaction_type: 'purchase',
    quantity: 20,
    unit_price: 3.25,
    total_price: 65,
    reference_number: 'PO-2025-008',
    notes: 'Restocking cleaning supplies',
    department: 'Maintenance',
    transaction_date: '2025-03-22T09:15:00Z',
    item: { name: 'Liquid Soap', sku: 'CL-LS-003' }
  }
];

const InventoryContext = createContext();

export const useInventory = () => useContext(InventoryContext);

export const InventoryProvider = ({ children }) => {
  const { user } = useUser();
  const [inventoryItems, setInventoryItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  // Fetch all inventory items
  const fetchInventoryItems = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*, inventory_categories(id, name)')
        .order('name');

      if (error) throw error;
      setInventoryItems(data || []);
      setIsUsingMockData(false);
      return data;
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setError(error.message);
      toast.error(`Failed to fetch inventory data from server. Using demo data instead.`);
      // Use mock data if the connection fails
      setInventoryItems(MOCK_ITEMS);
      setIsUsingMockData(true);
      return MOCK_ITEMS;
    } finally {
      setLoading(false);
    }
  };

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('inventory_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
      return data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(error.message);
      // Use mock data if the connection fails
      setCategories(MOCK_CATEGORIES);
      return MOCK_CATEGORIES;
    } finally {
      setLoading(false);
    }
  };

  // Fetch all transactions
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('inventory_transactions')
        .select('*, item:item_id(name, sku)')
        .order('transaction_date', { ascending: false });

      if (error) throw error;
      setTransactions(data || []);
      return data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError(error.message);
      // Use mock data if the connection fails
      setTransactions(MOCK_TRANSACTIONS);
      return MOCK_TRANSACTIONS;
    } finally {
      setLoading(false);
    }
  };

  // Add a new inventory item
  const addInventoryItem = async (itemData) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate adding an item in mock mode
        const newItem = {
          id: Date.now().toString(),
          ...itemData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          inventory_categories: categories.find(c => c.id === itemData.category_id) || null
        };
        
        setInventoryItems(prev => [...prev, newItem]);
        toast.success('Item added successfully (Demo Mode)');
        return { data: newItem, error: null };
      }
      
      const { data, error } = await supabase
        .from('inventory_items')
        .insert([itemData])
        .select('*, inventory_categories(id, name)')
        .single();

      if (error) throw error;
      
      setInventoryItems(prev => [...prev, data]);
      toast.success('Item added successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error adding inventory item:', error);
      setError(error.message);
      toast.error(`Failed to add item: ${error.message}`);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Update an existing inventory item
  const updateInventoryItem = async (id, itemData) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate updating an item in mock mode
        const updatedItems = inventoryItems.map(item => 
          item.id === id ? { 
            ...item, 
            ...itemData, 
            updated_at: new Date().toISOString(),
            inventory_categories: categories.find(c => c.id === itemData.category_id) || item.inventory_categories
          } : item
        );
        
        setInventoryItems(updatedItems);
        toast.success('Item updated successfully (Demo Mode)');
        return { data: updatedItems.find(item => item.id === id), error: null };
      }
      
      const { data, error } = await supabase
        .from('inventory_items')
        .update(itemData)
        .eq('id', id)
        .select('*, inventory_categories(id, name)')
        .single();

      if (error) throw error;
      
      setInventoryItems(prev => prev.map(item => item.id === id ? data : item));
      toast.success('Item updated successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error updating inventory item:', error);
      setError(error.message);
      toast.error(`Failed to update item: ${error.message}`);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Delete an inventory item
  const deleteInventoryItem = async (id) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate deleting an item in mock mode
        setInventoryItems(prev => prev.filter(item => item.id !== id));
        toast.success('Item deleted successfully (Demo Mode)');
        return { error: null };
      }
      
      const { error } = await supabase
        .from('inventory_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setInventoryItems(prev => prev.filter(item => item.id !== id));
      toast.success('Item deleted successfully');
      return { error: null };
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      setError(error.message);
      toast.error(`Failed to delete item: ${error.message}`);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Add a new category
  const addCategory = async (categoryData) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate adding a category in mock mode
        const newCategory = {
          id: Date.now().toString(),
          ...categoryData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setCategories(prev => [...prev, newCategory]);
        toast.success('Category added successfully (Demo Mode)');
        return { data: newCategory, error: null };
      }
      
      const { data, error } = await supabase
        .from('inventory_categories')
        .insert([categoryData])
        .select()
        .single();

      if (error) throw error;
      
      setCategories(prev => [...prev, data]);
      toast.success('Category added successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error adding category:', error);
      setError(error.message);
      toast.error(`Failed to add category: ${error.message}`);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Update an existing category
  const updateCategory = async (id, categoryData) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate updating a category in mock mode
        const updatedCategories = categories.map(category => 
          category.id === id ? { 
            ...category, 
            ...categoryData, 
            updated_at: new Date().toISOString() 
          } : category
        );
        
        setCategories(updatedCategories);
        toast.success('Category updated successfully (Demo Mode)');
        return { data: updatedCategories.find(category => category.id === id), error: null };
      }
      
      const { data, error } = await supabase
        .from('inventory_categories')
        .update(categoryData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setCategories(prev => prev.map(category => category.id === id ? data : category));
      toast.success('Category updated successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error updating category:', error);
      setError(error.message);
      toast.error(`Failed to update category: ${error.message}`);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Delete a category
  const deleteCategory = async (id) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate deleting a category in mock mode
        setCategories(prev => prev.filter(category => category.id !== id));
        toast.success('Category deleted successfully (Demo Mode)');
        return { error: null };
      }
      
      const { error } = await supabase
        .from('inventory_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setCategories(prev => prev.filter(category => category.id !== id));
      toast.success('Category deleted successfully');
      return { error: null };
    } catch (error) {
      console.error('Error deleting category:', error);
      setError(error.message);
      toast.error(`Failed to delete category: ${error.message}`);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Add a new transaction
  const addTransaction = async (transactionData) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate adding a transaction in mock mode
        const newTransaction = {
          id: Date.now().toString(),
          ...transactionData,
          transaction_date: transactionData.transaction_date || new Date().toISOString(),
          created_at: new Date().toISOString(),
          item: inventoryItems.find(item => item.id === transactionData.item_id) || { name: 'Unknown Item', sku: 'UNKNOWN' }
        };
        
        setTransactions(prev => [newTransaction, ...prev]);
        
        // Update item stock based on transaction type
        const updatedItems = [...inventoryItems];
        const itemIndex = updatedItems.findIndex(item => item.id === transactionData.item_id);
        
        if (itemIndex !== -1) {
          const item = updatedItems[itemIndex];
          if (transactionData.transaction_type === 'purchase') {
            item.current_stock += transactionData.quantity;
          } else if (transactionData.transaction_type === 'consumption') {
            item.current_stock -= transactionData.quantity;
          } else if (transactionData.transaction_type === 'adjustment') {
            item.current_stock += transactionData.quantity; // Can be negative for stock reduction
          }
          
          updatedItems[itemIndex] = { ...item, updated_at: new Date().toISOString() };
          setInventoryItems(updatedItems);
        }
        
        toast.success('Transaction recorded successfully (Demo Mode)');
        return { data: newTransaction, error: null };
      }
      
      const { data, error } = await supabase
        .from('inventory_transactions')
        .insert([transactionData])
        .select('*, item:item_id(name, sku)')
        .single();

      if (error) throw error;
      
      setTransactions(prev => [data, ...prev]);
      
      // Refresh inventory items to get updated stock
      fetchInventoryItems();
      
      toast.success('Transaction recorded successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error adding transaction:', error);
      setError(error.message);
      toast.error(`Failed to record transaction: ${error.message}`);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Update an existing transaction
  const updateTransaction = async (id, transactionData) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate updating a transaction in mock mode
        // First, get the original transaction to calculate stock changes
        const originalTransaction = transactions.find(transaction => transaction.id === id);
        
        // Update the transaction in the state
        const updatedTransactions = transactions.map(transaction => 
          transaction.id === id ? {
            ...transaction,
            ...transactionData,
            updated_at: new Date().toISOString()
          } : transaction
        );
        
        setTransactions(updatedTransactions);
        
        // Update item stock based on transaction changes
        if (originalTransaction && originalTransaction.item_id === transactionData.item_id) {
          const updatedItems = [...inventoryItems];
          const itemIndex = updatedItems.findIndex(item => item.id === transactionData.item_id);
          
          if (itemIndex !== -1) {
            const item = updatedItems[itemIndex];
            const quantityDiff = transactionData.quantity - originalTransaction.quantity;
            
            if (transactionData.transaction_type === 'purchase') {
              item.current_stock += quantityDiff;
            } else if (transactionData.transaction_type === 'consumption') {
              item.current_stock -= quantityDiff;
            } else if (transactionData.transaction_type === 'adjustment') {
              item.current_stock += quantityDiff;
            }
            
            updatedItems[itemIndex] = { ...item, updated_at: new Date().toISOString() };
            setInventoryItems(updatedItems);
          }
        }
        
        toast.success('Transaction updated successfully (Demo Mode)');
        return { data: updatedTransactions.find(transaction => transaction.id === id), error: null };
      }
      
      const { data, error } = await supabase
        .from('inventory_transactions')
        .update(transactionData)
        .eq('id', id)
        .select('*, item:item_id(name, sku)')
        .single();

      if (error) throw error;
      
      setTransactions(prev => prev.map(transaction => transaction.id === id ? data : transaction));
      
      // Refresh inventory items to get updated stock
      fetchInventoryItems();
      
      toast.success('Transaction updated successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error updating transaction:', error);
      setError(error.message);
      toast.error(`Failed to update transaction: ${error.message}`);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Delete a transaction
  const deleteTransaction = async (id) => {
    try {
      setLoading(true);
      
      if (isUsingMockData) {
        // Simulate deleting a transaction in mock mode
        // First, get the transaction to be deleted to revert the stock change
        const transactionToDelete = transactions.find(transaction => transaction.id === id);
        
        // Remove the transaction from the state
        setTransactions(prev => prev.filter(transaction => transaction.id !== id));
        
        // Revert the stock changes caused by this transaction
        if (transactionToDelete) {
          const updatedItems = [...inventoryItems];
          const itemIndex = updatedItems.findIndex(item => item.id === transactionToDelete.item_id);
          
          if (itemIndex !== -1) {
            const item = updatedItems[itemIndex];
            
            if (transactionToDelete.transaction_type === 'purchase') {
              item.current_stock -= transactionToDelete.quantity;
            } else if (transactionToDelete.transaction_type === 'consumption') {
              item.current_stock += transactionToDelete.quantity;
            } else if (transactionToDelete.transaction_type === 'adjustment') {
              item.current_stock -= transactionToDelete.quantity; // Reverse the adjustment
            }
            
            updatedItems[itemIndex] = { ...item, updated_at: new Date().toISOString() };
            setInventoryItems(updatedItems);
          }
        }
        
        toast.success('Transaction deleted successfully (Demo Mode)');
        return { error: null };
      }
      
      const { error } = await supabase
        .from('inventory_transactions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTransactions(prev => prev.filter(transaction => transaction.id !== id));
      
      // Refresh inventory items to get updated stock
      fetchInventoryItems();
      
      toast.success('Transaction deleted successfully');
      return { error: null };
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setError(error.message);
      toast.error(`Failed to delete transaction: ${error.message}`);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Check for low stock items
  const checkLowStockItems = () => {
    const lowStock = inventoryItems.filter(item => item.current_stock <= item.reorder_point);
    setLowStockItems(lowStock);
    return lowStock;
  };

  // Initialize data
  useEffect(() => {
    if (user) {
      const initializeData = async () => {
        await fetchInventoryItems();
        await fetchCategories();
        await fetchTransactions();
        checkLowStockItems();
      };
      
      initializeData();
    }
  }, [user]);

  // Check for low stock whenever inventory items change
  useEffect(() => {
    checkLowStockItems();
  }, [inventoryItems]);

  return (
    <InventoryContext.Provider
      value={{
        inventoryItems,
        categories,
        transactions,
        purchaseOrders,
        suppliers,
        loading,
        error,
        lowStockItems,
        isUsingMockData,
        fetchInventoryItems,
        fetchCategories,
        fetchTransactions,
        addInventoryItem,
        updateInventoryItem,
        deleteInventoryItem,
        addCategory,
        updateCategory,
        deleteCategory,
        addTransaction,
        updateTransaction,
        deleteTransaction,
        checkLowStockItems
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
};

export default InventoryContext;
